// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './Book.scss';
// Component imports:
import Table from 'react-bootstrap/Table';
// Service imports:
import { deleteOrderFromBook, formatNumber, insertOrderForBook } from '../../services/utils';
// Action imports:
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { PublicOrderTickData } from '../../types/api/PublicTypes';
import { WithBook } from '../../types/api/Types';

interface IOwnProps {
    sortedPublicOrderTickData: Array<WithBook<PublicOrderTickData>>
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

interface IState {
    hoveredBookEntry: PublicOrderTickData | null
};

class Book extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hoveredBookEntry: null
        };
    }

    tableRows = (
        bidTdClassName: any,
        askTdClassName: any, 
        bidOrder: PublicOrderTickData | undefined, 
        askOrder: PublicOrderTickData | undefined,
        i: number
    ) => {
        const { setSelectedTick } = this.props;
        return (
            <tr key={i}>
                <td
                    className={bidTdClassName}
                    onClick={() => {
                        if (bidOrder) setSelectedTick(bidOrder);
                    }}
                    onMouseEnter={() => { this.setState({ hoveredBookEntry: bidOrder || null }); }}
                    onMouseLeave={() => { this.setState({ hoveredBookEntry: null }); }}
                >
                    {(bidOrder) ? formatNumber(bidOrder.quantity, 'volume') : null}
                </td>
                <td
                    className={bidTdClassName}
                    onClick={() => {
                        if (bidOrder) setSelectedTick(bidOrder);
                    }}
                    onMouseEnter={() => { this.setState({ hoveredBookEntry: bidOrder || null }); }}
                    onMouseLeave={() => { this.setState({ hoveredBookEntry: null }); }}
                >
                    {(bidOrder) ? formatNumber(bidOrder.price) : null}
                </td>
                <td
                    className={askTdClassName}
                    onClick={() => { if (askOrder) setSelectedTick(askOrder) }}
                    onMouseEnter={() => { this.setState({ hoveredBookEntry: askOrder || null }); }}
                    onMouseLeave={() => { this.setState({ hoveredBookEntry: null }); }}
                >
                    {(askOrder) ? formatNumber(askOrder.price) : null}
                </td>
                <td
                    className={askTdClassName}
                    onClick={() => { if (askOrder) setSelectedTick(askOrder); }}
                    onMouseEnter={() => { this.setState({ hoveredBookEntry: askOrder || null }); }}
                    onMouseLeave={() => { this.setState({ hoveredBookEntry: null }); }}
                >
                    {(askOrder) ? formatNumber(askOrder.quantity, 'volume') : null}
                </td>
            </tr>
        );
    }

    bookTickDataEntryControler = (partialOrderHistoryTickData: WithBook<PublicOrderTickData>[], ) => {
        let bidOrdersInPriorityOrder: PublicOrderTickData[] = [];
        let askOrdersInPriorityOrder: PublicOrderTickData[] = [];
    
        for (let bookTickDataEntry of partialOrderHistoryTickData) {
            const { tick } = bookTickDataEntry;
            const { side } = tick;
            // Insert.
            if (tick.update_code === 'Insert') {
                if (side === 'bid') {
                    bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
                } else {
                    askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
                }
            }
            // Update.
            else if (tick.update_code === 'Update') {
                if (side === 'bid') {
                    bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
                    bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
                } else {
                    askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
                    askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
                }
            }
            // Delete.
            else {
                if (side === 'bid') {
                    bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
                } else {
                    askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
                }
            }
        }

        return { bidOrdersInPriorityOrder, askOrdersInPriorityOrder };
    }

    getBookRows = () => {
        const { hoveredBookEntry } = this.state;
        const { sortedPublicOrderTickData, selectedTick } = this.props;

        // Try to find the selected order. If the selected tick is not OrderTickData, then cut off the orders at the timestamp.
        let usingSelectedOrder = false;
        const indexOfSelectedOrder = sortedPublicOrderTickData.findIndex(({ tick }) => {
            if (selectedTick === null) return false;
            if (tick === selectedTick) {
                usingSelectedOrder = true;
                return true;
            }
            if (selectedTick.timestamp !== null && tick.timestamp !== null && new Date(selectedTick.timestamp) < new Date(tick.timestamp)) {
                return true;
            }
            return false;
        });

        if (indexOfSelectedOrder === -1) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={4}>
                        Empty book
                    </td>
                </tr>
            )
        }

        // Selected order cuts off AFTER the selected order. Selected non-order cuts off BEFORE the higher timestamp order.
        const partialOrderHistoryTickData = sortedPublicOrderTickData.slice(0, indexOfSelectedOrder + (usingSelectedOrder ? 1 : 0));

        const { bidOrdersInPriorityOrder, askOrdersInPriorityOrder } = this.bookTickDataEntryControler(partialOrderHistoryTickData);

        const sizeOfBiggerArray = Math.max(bidOrdersInPriorityOrder.length, askOrdersInPriorityOrder.length);

        if (sizeOfBiggerArray === 0) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={4}>
                        Empty book
                    </td>
                </tr>
            )
        }

        const jsxRows: JSX.Element[] = [];

        for (let i = 0; i < sizeOfBiggerArray; i++) {
            const bidOrder: PublicOrderTickData | undefined = bidOrdersInPriorityOrder[i] as PublicOrderTickData | undefined;
            const askOrder: PublicOrderTickData | undefined = askOrdersInPriorityOrder[i] as PublicOrderTickData | undefined;
            const bidTdClassName = (selectedTick === bidOrder && hoveredBookEntry === bidOrder) // if
                ? 'CurrentHover'
                : (selectedTick === bidOrder) // else if
                ? 'Current'
                : (hoveredBookEntry === bidOrder) // else if
                ? 'Hover'
                : undefined;
            const askTdClassName = (selectedTick === askOrder && hoveredBookEntry === askOrder) // if
                ? 'CurrentHover'
                : (selectedTick === askOrder) // else if 
                ? 'Current'
                : (hoveredBookEntry === askOrder) // else if
                ? 'Hover'
                : undefined;
            jsxRows.push(
                this.tableRows(bidTdClassName, askTdClassName, bidOrder, askOrder, i)
            );
        }

        return jsxRows;
    }

    render () {
        return (
            <div id='Book'>
                <h5 className='SectionTitle'>Order Book</h5>
                <Table className='CompressedTable NotHover'>
                    <thead>
                        <tr>
                            <th scope='col'>Qty.</th>
                            <th scope='col'>Bid</th>
                            <th scope='col'>Ask</th>
                            <th scope='col'>Qty.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.getBookRows()}
                    </tbody>
                </Table>
            </div>
        );
    }
}

function mapStateToProps({ dashboardReducer }: RootState) {
    const { selectedTick } = dashboardReducer;
    return {
        selectedTick
    };
}

const connector = connect(mapStateToProps, { setSelectedTick });
export default connector(Book);
